import {
  LOGIN_FAILURE_CODES,
  loginCredentialsSchema,
  type LoginCredentials,
  type LoginFailureCode,
} from "@jasper/client-models";
import { JasperWordmark, validateSchema } from "@jasper/forms";
import { Button, PasswordInput, Text, TextInput, Title } from "@mantine/core";
import { createStyles } from "@mantine/emotion";
import { notifications } from "@mantine/notifications";
import { IconArrowRight } from "@tabler/icons-react";
import { useForm } from "@tanstack/react-form";

const HOTLINE_PHONE_NUMBER = "06 52 21 69 29";

const DEFAULT_VALUES: LoginCredentials = { email: "", password: "" };

const LOGIN_FAILURE_MESSAGES: Record<LoginFailureCode, string> = {
  [LOGIN_FAILURE_CODES.EMPTY_EMAIL]: "Veuillez saisir votre adresse e-mail.",
  [LOGIN_FAILURE_CODES.INVALID_EMAIL]: "Adresse e-mail invalide.",
  [LOGIN_FAILURE_CODES.EMPTY_PASSWORD]: "Veuillez saisir votre mot de passe.",
};

// Field errors hold the schema's failure codes; unknown values are shown as-is.
const toLoginFailureMessage = (error: string | undefined) =>
  error !== undefined && error in LOGIN_FAILURE_MESSAGES
    ? LOGIN_FAILURE_MESSAGES[error as LoginFailureCode]
    : error;

export interface LoginPageProps {
  /** Logs the user in. Must throw when the credentials are rejected. */
  onLogin: (data: LoginCredentials) => Promise<void>;
}

const useStyles = createStyles(theme => ({
  formSide: {
    position: "relative",
    overflow: "hidden",
    display: "flex",
    alignItems: "center",
    background: theme.colors.appBackground[0],
    // Soft coloured blobs drifting behind the form.
    "&::before": {
      content: '""',
      position: "absolute",
      inset: "-12%",
      pointerEvents: "none",
      background: [
        "radial-gradient(38% 42% at 18% 26%, rgba(61,79,224,.22), transparent 70%)",
        "radial-gradient(34% 40% at 82% 18%, rgba(139,92,246,.18), transparent 72%)",
        "radial-gradient(42% 46% at 72% 84%, rgba(245,158,11,.16), transparent 70%)",
        "radial-gradient(36% 38% at 12% 88%, rgba(16,185,129,.12), transparent 72%)",
      ].join(","),
      filter: "blur(28px)",
      animation:
        "loginDrift 24s cubic-bezier(0.22, 1, 0.36, 1) infinite alternate",
      "@media (prefers-reduced-motion: reduce)": { animation: "none" },
    },
  },
  form: {
    position: "relative",
    zIndex: 1,
    width: "100%",
    maxWidth: 480,
    margin: "0 auto",
    padding: "64px 48px",
  },
}));

export const LoginContent = ({ onLogin }: LoginPageProps) => {
  const { classes } = useStyles();

  const form = useForm({
    defaultValues: DEFAULT_VALUES,
    validators: {
      // Runs before `onSubmit`: `onLogin` is only called if the schema passes.
      onSubmit: ({ value }) => {
        const result = validateSchema(loginCredentialsSchema, value);
        if (result.success === true) {
          return undefined;
        }

        // Dispatches each schema error to its own field (`email`, `password`).
        return { fields: result.errors ?? {} };
      },
    },
    onSubmit: async ({ value }) => {
      try {
        // Cannot throw: the validator above already passed. Parsing only
        // applies the schema's transforms (trimmed email) to the sent values.
        await onLogin(loginCredentialsSchema.parse(value));
      } catch (e) {
        console.error(e);
        notifications.show({
          color: "red",
          title: "Identifiants incorrects",
          message: "",
        });
      }
    },
  });

  return (
    <div className={classes.formSide}>
      <form
        onSubmit={event => {
          event.preventDefault();
          form.handleSubmit();
        }}
        className={classes.form}
      >
        <JasperWordmark size={25} />
        <Title order={1} variant="display" size="lg" mb="s4" mt="s16">
          Bon retour.
        </Title>
        <Text variant="small" c="ink.5" mb="s8">
          Connectez-vous pour retrouver vos commandes et vos empreintes.
        </Text>

        <form.Field name="email">
          {field => (
            <TextInput
              name={field.name}
              value={field.state.value}
              onChange={event => field.handleChange(event.currentTarget.value)}
              onBlur={field.handleBlur}
              error={toLoginFailureMessage(field.state.meta.errors[0])}
              label="Adresse e-mail"
              placeholder="nom@cabinet.fr"
              autoComplete="username"
              mb="s4"
              data-testid="input-email"
            />
          )}
        </form.Field>
        <form.Field name="password">
          {field => (
            <PasswordInput
              name={field.name}
              value={field.state.value}
              onChange={event => field.handleChange(event.currentTarget.value)}
              onBlur={field.handleBlur}
              error={toLoginFailureMessage(field.state.meta.errors[0])}
              label="Mot de passe"
              placeholder="Mot de passe"
              autoComplete="current-password"
              data-testid="input-password"
              aria-invalid={Boolean(field.state.meta.errors[0])}
            />
          )}
        </form.Field>

        <form.Subscribe selector={state => state.isSubmitting}>
          {isSubmitting => (
            <Button
              type="submit"
              fullWidth
              mt="s6"
              h={44}
              rightSection={<IconArrowRight size={16} />}
              loading={isSubmitting}
              data-testid="submit-login"
            >
              Se connecter
            </Button>
          )}
        </form.Subscribe>

        <Text variant="caption" size="md" c="ink.5" lh={1.6} mt="s6">
          Pas encore de compte&nbsp;? Appelez la hotline au{" "}
          <b>{HOTLINE_PHONE_NUMBER}</b>.
        </Text>
      </form>
    </div>
  );
};
